import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { PnlDisplay } from '@/components/ui/PnlDisplay';
import { StrategyBadge } from '@/components/ui/StrategyBadge';
import { StatusBadge } from '@/components/ui/StatusBadge';
import type { StrategyType, ChainStatus } from '@steward/shared';
import { analyticsApi } from '@/lib/api/client';
import { formatCurrency, formatDate } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { useUrlParam } from '@/hooks/useUrlState';

interface SymbolChain {
  id: string;
  strategyType: string;
  status: string;
  openedAt: Date;
  closedAt: Date | null;
  realizedPnl: number;
  netPremium: number;
}

interface SymbolInsight {
  symbol: string;
  tradesCount: number;
  openCount: number;
  closedCount: number;
  totalPnl: number;
  premiumCollected: number;
  premiumPaid: number;
  netPremium: number;
  winCount: number;
  lossCount: number;
  winRate: number | null;
  firstTradeAt: Date;
  lastTradeAt: Date;
  lastClosedAt: Date | null;
  daysSinceLastTrade: number;
  strategies: string[];
  chains: SymbolChain[];
  pnlProgression: Array<{ date: string; pnl: number; cumPnl: number }>;
}

type SortField = 'totalPnl' | 'tradesCount' | 'winRate' | 'netPremium' | 'daysSinceLastTrade';

const SORT_FIELDS: readonly SortField[] = ['totalPnl', 'tradesCount', 'winRate', 'netPremium', 'daysSinceLastTrade'];

function useSymbolInsights() {
  return useQuery({
    queryKey: ['analytics', 'symbol-insights'],
    queryFn: () => analyticsApi.getSymbolInsights(),
    staleTime: 5 * 60 * 1000,
  });
}

function MiniPnlChart({ progression }: { progression: Array<{ cumPnl: number }> }) {
  if (progression.length < 2) return null;

  const values = progression.map(p => p.cumPnl);
  const min = Math.min(...values, 0);
  const max = Math.max(...values, 0);
  const range = max - min || 1;
  const height = 32;
  const width = 80;

  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * width;
    const y = height - ((v - min) / range) * height;
    return `${x},${y}`;
  }).join(' ');

  const finalColor = values[values.length - 1] >= 0 ? '#16a34a' : '#dc2626';

  return (
    <svg width={width} height={height} className="inline-block">
      <polyline points={points} fill="none" stroke={finalColor} strokeWidth="1.5" />
    </svg>
  );
}

function SymbolDetail({ insight }: { insight: SymbolInsight }) {
  // Find the "turning point" — where cumPnl peaked before declining
  const peakIdx = insight.pnlProgression.reduce((best, p, i) =>
    p.cumPnl > (insight.pnlProgression[best]?.cumPnl ?? -Infinity) ? i : best, 0);
  const peak = insight.pnlProgression[peakIdx];
  const hasTurndown = peak && peakIdx < insight.pnlProgression.length - 1 &&
    insight.pnlProgression[insight.pnlProgression.length - 1].cumPnl < peak.cumPnl * 0.7;

  return (
    <div className="space-y-4">
      {/* Key stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="text-center">
          <p className="text-xs text-gray-500">Win Rate</p>
          <p className="text-lg font-semibold text-gray-900">
            {insight.winRate !== null ? `${insight.winRate}%` : '-'}
          </p>
          <p className="text-xs text-gray-400">{insight.winCount}W / {insight.lossCount}L</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-500">Net Premium</p>
          <p className={cn('text-lg font-semibold', insight.netPremium >= 0 ? 'text-green-600' : 'text-red-600')}>
            {formatCurrency(insight.netPremium)}
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-500">Active Since</p>
          <p className="text-sm font-medium text-gray-900">{formatDate(insight.firstTradeAt)}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-500">Last Trade</p>
          <p className="text-sm font-medium text-gray-900">{formatDate(insight.lastTradeAt)}</p>
          {insight.daysSinceLastTrade > 60 && (
            <p className="text-xs text-amber-600">{insight.daysSinceLastTrade}d ago</p>
          )}
        </div>
      </div>

      {/* Turning point callout */}
      {hasTurndown && peak && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm">
          <span className="font-medium text-amber-800">Peak P&L was {formatCurrency(peak.cumPnl)}</span>
          <span className="text-amber-600"> on {peak.date} — now at {formatCurrency(insight.pnlProgression[insight.pnlProgression.length - 1].cumPnl)}</span>
        </div>
      )}

      {/* Strategies used */}
      <div className="flex flex-wrap gap-1">
        {insight.strategies.map(s => (
          <StrategyBadge key={s} strategy={s as StrategyType} size="sm" />
        ))}
      </div>

      {/* Chain history */}
      <div>
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          Chains ({insight.chains.length})
        </h4>
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {insight.chains.map((chain) => (
            <Link
              key={chain.id}
              to={`/chains/${chain.id}`}
              className="flex items-center justify-between gap-2 px-3 py-1.5 rounded hover:bg-gray-100 text-sm"
            >
              <div className="flex items-center gap-2">
                <StrategyBadge strategy={chain.strategyType as StrategyType} size="sm" />
                <StatusBadge status={chain.status as ChainStatus} size="sm" />
                <span className="text-xs text-gray-500">
                  {formatDate(chain.openedAt)}
                  {chain.closedAt ? ` – ${formatDate(chain.closedAt)}` : ''}
                </span>
              </div>
              {chain.status === 'closed' ? (
                <PnlDisplay value={chain.realizedPnl} size="sm" />
              ) : (
                <span className={cn('font-mono text-xs', chain.netPremium >= 0 ? 'text-green-600' : 'text-red-600')}>
                  {formatCurrency(chain.netPremium)}
                </span>
              )}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export function InsightsPage() {
  const { data, isLoading } = useSymbolInsights();
  // Sort and the drilled-down symbol are the whole view here, so both live in the URL: the
  // interesting screen on this page is "SCHW, sorted by dormancy", and that should be a link.
  const [sortField, setSortField] = useUrlParam<SortField>('sort', 'totalPnl', SORT_FIELDS);
  const [expandedSymbol, setExpandedSymbol] = useUrlParam('symbol', '');

  const symbols = data?.symbols ?? [];
  const idleStocks = data?.idleStocks ?? [];

  const sorted = useMemo(() => {
    return [...symbols].sort((a, b) => {
      switch (sortField) {
        case 'totalPnl': return b.totalPnl - a.totalPnl;
        case 'tradesCount': return b.tradesCount - a.tradesCount;
        case 'winRate': return (b.winRate ?? 0) - (a.winRate ?? 0);
        case 'netPremium': return b.netPremium - a.netPremium;
        case 'daysSinceLastTrade': return b.daysSinceLastTrade - a.daysSinceLastTrade;
        default: return 0;
      }
    });
  }, [symbols, sortField]);

  // Compute highlights
  const topGainers = useMemo(() =>
    [...symbols].filter(s => s.closedCount > 0).sort((a, b) => b.totalPnl - a.totalPnl).slice(0, 5),
    [symbols]
  );
  const topLosers = useMemo(() =>
    [...symbols].filter(s => s.totalPnl < 0).sort((a, b) => a.totalPnl - b.totalPnl).slice(0, 5),
    [symbols]
  );
  const dormant = useMemo(() =>
    [...symbols].filter(s => s.daysSinceLastTrade > 60 && s.closedCount >= 3).sort((a, b) => b.totalPnl - a.totalPnl),
    [symbols]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Symbol Insights</h1>
        <p className="text-sm text-gray-500 mt-1">Performance analysis by underlying symbol</p>
      </div>

      {/* Highlights row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Top Gainers */}
        <Card>
          <CardHeader><CardTitle className="text-sm">Top Performers</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-gray-50">
              {topGainers.map(s => (
                <div
                  key={s.symbol}
                  className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedSymbol(expandedSymbol === s.symbol ? '' : s.symbol)}
                >
                  <div className="flex items-center gap-2">
                    <Link to={`/symbols/${s.symbol}`} className="font-semibold text-brand-600 text-sm" onClick={e => e.stopPropagation()}>
                      {s.symbol}
                    </Link>
                    <MiniPnlChart progression={s.pnlProgression} />
                  </div>
                  <PnlDisplay value={s.totalPnl} size="sm" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Losers */}
        <Card>
          <CardHeader><CardTitle className="text-sm">Biggest Losses</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-gray-50">
              {topLosers.map(s => (
                <div
                  key={s.symbol}
                  className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedSymbol(expandedSymbol === s.symbol ? '' : s.symbol)}
                >
                  <div className="flex items-center gap-2">
                    <Link to={`/symbols/${s.symbol}`} className="font-semibold text-brand-600 text-sm" onClick={e => e.stopPropagation()}>
                      {s.symbol}
                    </Link>
                    <MiniPnlChart progression={s.pnlProgression} />
                  </div>
                  <PnlDisplay value={s.totalPnl} size="sm" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Dormant & Idle Stocks */}
        <Card>
          <CardHeader><CardTitle className="text-sm">Dormant Symbols</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-gray-50">
              {dormant.slice(0, 5).map(s => (
                <div
                  key={s.symbol}
                  className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedSymbol(expandedSymbol === s.symbol ? '' : s.symbol)}
                >
                  <div>
                    <Link to={`/symbols/${s.symbol}`} className="font-semibold text-brand-600 text-sm" onClick={e => e.stopPropagation()}>
                      {s.symbol}
                    </Link>
                    <p className="text-xs text-gray-400">{s.daysSinceLastTrade}d idle · {s.closedCount} trades</p>
                  </div>
                  <PnlDisplay value={s.totalPnl} size="sm" />
                </div>
              ))}
              {idleStocks.length > 0 && (
                <div className="px-4 py-2">
                  <p className="text-xs font-medium text-amber-700">Stock held, no active options:</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {idleStocks.map(sym => (
                      <Link key={sym} to={`/symbols/${sym}`} className="text-xs px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded">
                        {sym}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Expanded detail */}
      {expandedSymbol && (() => {
        const insight = symbols.find(s => s.symbol === expandedSymbol);
        if (!insight) return null;
        return (
          <Card className="border-brand-200" data-testid="insight-detail" data-symbol={insight.symbol}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Link to={`/symbols/${insight.symbol}`} className="text-brand-600 hover:text-brand-700">
                    {insight.symbol}
                  </Link>
                  <span className="text-sm font-normal text-gray-500">
                    {insight.tradesCount} chains · {insight.closedCount} closed
                  </span>
                </CardTitle>
                <PnlDisplay value={insight.totalPnl} size="lg" />
              </div>
            </CardHeader>
            <CardContent>
              <SymbolDetail insight={insight} />
            </CardContent>
          </Card>
        );
      })()}

      {/* Full symbol table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>All Symbols</CardTitle>
            <div className="flex gap-1">
              {([
                ['totalPnl', 'P&L'],
                ['tradesCount', 'Trades'],
                ['winRate', 'Win Rate'],
                ['netPremium', 'Premium'],
                ['daysSinceLastTrade', 'Dormancy'],
              ] as [SortField, string][]).map(([field, label]) => (
                <Button
                  key={field}
                  variant={sortField === field ? 'primary' : 'ghost'}
                  size="sm"
                  aria-pressed={sortField === field}
                  onClick={() => setSortField(field)}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 border-b border-gray-200">
                  <th className="text-left py-2 px-4 font-medium">Symbol</th>
                  <th className="text-center py-2 px-4 font-medium">Trades</th>
                  <th className="text-center py-2 px-4 font-medium">Open</th>
                  <th className="text-center py-2 px-4 font-medium">Win Rate</th>
                  <th className="text-right py-2 px-4 font-medium">Net Premium</th>
                  <th className="text-right py-2 px-4 font-medium">Realized P&L</th>
                  <th className="text-center py-2 px-4 font-medium">Trend</th>
                  <th className="text-right py-2 px-4 font-medium">Last Trade</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((s) => {
                  const isExpanded = expandedSymbol === s.symbol;
                  return (
                    <tr
                      key={s.symbol}
                      data-testid={`insight-row-${s.symbol}`}
                      aria-expanded={isExpanded}
                      className={cn(
                        'border-b border-gray-50 cursor-pointer transition-colors',
                        isExpanded ? 'bg-brand-50' : 'hover:bg-gray-50'
                      )}
                      onClick={() => setExpandedSymbol(isExpanded ? '' : s.symbol)}
                    >
                      <td className="py-2.5 px-4">
                        <div className="flex items-center gap-1.5">
                          <svg
                            className={cn('w-3 h-3 text-gray-400 transition-transform', isExpanded && 'rotate-90')}
                            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                          <Link
                            to={`/symbols/${s.symbol}`}
                            className="font-semibold text-brand-600 hover:text-brand-700"
                            onClick={e => e.stopPropagation()}
                          >
                            {s.symbol}
                          </Link>
                          <div className="flex gap-0.5">
                            {s.strategies.slice(0, 2).map(st => (
                              <StrategyBadge key={st} strategy={st as StrategyType} size="sm" />
                            ))}
                            {s.strategies.length > 2 && (
                              <span className="text-[10px] text-gray-400">+{s.strategies.length - 2}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-2.5 px-4 text-center text-gray-600">{s.tradesCount}</td>
                      <td className="py-2.5 px-4 text-center text-gray-600">{s.openCount || '-'}</td>
                      <td className="py-2.5 px-4 text-center">
                        {s.winRate !== null ? (
                          <span className={cn('font-medium', s.winRate >= 60 ? 'text-green-600' : s.winRate < 40 ? 'text-red-600' : 'text-gray-600')}>
                            {s.winRate}%
                          </span>
                        ) : '-'}
                      </td>
                      <td className="py-2.5 px-4 text-right">
                        <span className={cn('font-mono text-xs', s.netPremium >= 0 ? 'text-green-600' : 'text-red-600')}>
                          {formatCurrency(s.netPremium)}
                        </span>
                      </td>
                      <td className="py-2.5 px-4 text-right">
                        <PnlDisplay value={s.totalPnl} size="sm" />
                      </td>
                      <td className="py-2.5 px-4 text-center">
                        <MiniPnlChart progression={s.pnlProgression} />
                      </td>
                      <td className="py-2.5 px-4 text-right">
                        <span className={cn('text-xs', s.daysSinceLastTrade > 90 ? 'text-amber-600' : 'text-gray-500')}>
                          {s.daysSinceLastTrade}d ago
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
